import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  App,
  Upload,
  Select,
  Switch,
  Row,
  Col,
  InputNumber,
  Modal,
} from "antd";
import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import API from "../../../services/api";
import { useCloudinaryUpload } from "../../../hooks/useCloudinaryUpload";
import { useState, useRef, useCallback, useEffect } from "react";
import "./AddBusiness.css";
import { useNavigate } from "react-router-dom";
import LocationField from "../../LocationField";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const INDUSTRY_OPTIONS = [
  "food",
  "retail",
  "education",
  "healthcare",
  "technology",
  "fitness",
  "beauty",
  "real_estate",
  "other",
];

const COMPANY_SIZE_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "500+"];
const BUSINESS_TYPE_OPTIONS = ["startup", "private", "public", "agency", "partnership", "other"];

function AddBusiness() {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);
  const timeoutRef = useRef(null);
  // cache company-name API results so Next button is instant on re-click
  const nameCheckCacheRef = useRef({});

  const {
    uploadedThumb: uploadedLogo,
    uploadedImages,
    imageFileList,
    setImageFileList,
    uploading,
    uploadThumbnail: uploadLogo,
    uploadImages,
    removeImage,
  } = useCloudinaryUpload("businesses", message);

  // Image upload errors
  const [logoError, setLogoError] = useState(false);
  const [imagesError, setImagesError] = useState(false);

  // Custom industry and business type
  const [customIndustry, setCustomIndustry] = useState("");
  const [showCustomIndustry, setShowCustomIndustry] = useState(false);
  const [customBusinessType, setCustomBusinessType] = useState("");
  const [showCustomBusinessType, setShowCustomBusinessType] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const getStepStyle = (step) => ({
    display: current === step ? "block" : "none",
    width: "100%",
  });

  const handleBack = () => {
    const values = form.getFieldsValue();
    const hasData = Object.values(values).some(
      (val) =>
        val !== undefined &&
        val !== null &&
        val !== "" &&
        !(Array.isArray(val) && val.length === 0)
    );
    if (!hasData) {
      window.history.back();
      return;
    }
    Modal.confirm({
      title: "Leave this page?",
      icon: <ExclamationCircleOutlined />,
      content: "All your unsaved changes will be lost if you go back.",
      okText: "Yes, go back",
      cancelText: "Stay",
      okButtonProps: { danger: true },
      onOk: () => {
        window.history.back();
      },
    });
  };

  const steps = [
    "Company",
    "Business",
    "Location",
    "Contact",
    "Social",
    "Images",
  ];

  const stepFields = [
    ["companyName", "tagline", "description", "companyUrl", "website", "foundedYear"],
    ["industry", "businessType", "companySize"],
    ["address", "locality", "city", "state", "country", "pincode", "latitude", "longitude"],
    ["contactName", "phone", "email"],
    ["linkedin", "instagram", "twitter"],
    ["logo", "images"],
  ];

  const validateCurrentStep = async () => {
    const fieldsToValidate = stepFields[current];

    try {
      if (current === 1) {
        if (showCustomIndustry && (!customIndustry || customIndustry.trim() === "")) {
          message.destroy();
          message.error("Please enter custom industry");
          return false;
        }
        if (showCustomBusinessType && (!customBusinessType || customBusinessType.trim() === "")) {
          message.destroy();
          message.error("Please enter custom business type");
          return false;
        }
      }

      if (current === 5) {
        if (!uploadedLogo) {
          setLogoError(true);
          message.error("Please upload a business logo");
          return false;
        }

        if (!uploadedImages || uploadedImages.length === 0) {
          setImagesError(true);
          message.error("Please upload at least one image");
          return false;
        }

        setLogoError(false);
        setImagesError(false);
      }

      // Only validate form fields if NOT step 5 (images)
      if (current !== 5) {
        await form.validateFields(fieldsToValidate);
      }
      return true;

    } catch (err) {
      let errorMessage = "Please fill all required fields";

      if (err?.errorFields?.length > 0) {
        errorMessage = err.errorFields[0]?.errors?.[0] || "Please fill all required fields correctly";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      message.destroy();
      message.error(errorMessage);
      return false;
    }
  };

  const next = async () => {
    const isValid = await validateCurrentStep();
    // FORCE STOP
    if (!isValid) return;

    setCurrent(current + 1);
    window.scrollTo(0, 0);
  };

  const prev = () => {
    setCurrent(current - 1);
    window.scrollTo(0, 0);
  };

  const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/;

  // optional URL validator — shows error when invalid, silent when empty
  const validateUrlOptional = async (_, value) => {
    if (!value || value.trim() === "") return Promise.resolve();

    let trimmed = value.trim();

    // Auto add https if missing
    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }

    // Strict regex validation
    if (!URL_REGEX.test(trimmed)) {
      return Promise.reject("Enter a valid URL (e.g. https://example.com)");
    }

    return Promise.resolve();
  };

  // optional tagline validator
  const validateTagline = async (_, value) => {
    if (!value || value.trim() === "") return Promise.resolve();
    const trimmed = value.trim();
    if (trimmed.length < 5) {
      return Promise.reject("Tagline must be at least 5 characters if provided");
    }
    if (trimmed.length > 120) {
      return Promise.reject("Tagline cannot exceed 120 characters");
    }
    if (!/^(?=.*[A-Za-z])[A-Za-z0-9\s.,&'!?:()-]+$/.test(trimmed)) {
      return Promise.reject("Tagline can only contain letters, numbers, spaces, and basic punctuation");
    }
    return Promise.resolve();
  };

  // social URL validator per-platform
  const validateSocialUrl = (platform) => async (_, value) => {
    if (!value || value.trim() === "") return Promise.resolve();

    let trimmed = value.trim();

    if (!/^https?:\/\//i.test(trimmed)) {
      trimmed = `https://${trimmed}`;
    }

    if (!URL_REGEX.test(trimmed)) {
      return Promise.reject(`Enter a valid ${platform} URL`);
    }

    return Promise.resolve();
  };

  // checkCompanyName with result cache
  const checkCompanyName = useCallback(async (_, value) => {
    if (!value || !value.trim()) {
      return Promise.reject("Company name is required");
    }
    if (value.trim().length < 3) {
      return Promise.reject("Company name must be at least 3 characters");
    }

    const trimmed = value.trim();

    // Serve from cache instantly
    if (nameCheckCacheRef.current[trimmed] !== undefined) {
      if (nameCheckCacheRef.current[trimmed]) {
        return Promise.reject("This company name is already registered");
      }
      return Promise.resolve();
    }

    clearTimeout(timeoutRef.current);

    return new Promise((resolve, reject) => {
      timeoutRef.current = setTimeout(async () => {
        try {
          const res = await API.get(
            `/businesses/check-name?name=${encodeURIComponent(trimmed)}`
          );
          nameCheckCacheRef.current[trimmed] = res.data.exists;
          if (res.data.exists) {
            reject("This company name is already registered");
          } else {
            resolve();
          }
        } catch {
          resolve(); // fail open on network error
        }
      }, 500);
    });
  }, []);

  const onFinish = async (values) => {
    try {
      setLoading(true);

      // Normalize URLs
      let normalizedWebsite = values.website || "";
      if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
        normalizedWebsite = `https://${normalizedWebsite}`;
      }

      let normalizedCompanyUrl = values.companyUrl || "";
      if (normalizedCompanyUrl && !/^https?:\/\//i.test(normalizedCompanyUrl)) {
        normalizedCompanyUrl = `https://${normalizedCompanyUrl}`;
      }

      const data = {
        companyName: values.companyName.trim(),
        tagline: values.tagline?.trim() || "",
        description: values.description.trim(),
        companyUrl: normalizedCompanyUrl,
        website: normalizedWebsite,
        foundedYear: values.foundedYear ? Number(values.foundedYear) : null,
        industry: values.industry,
        type: values.businessType,
        businessType: values.businessType,
        companySize: values.companySize,
        location: {
          address: values.address,
          locality: values.locality ? values.locality.trim().toLowerCase() : "",
          city: values.city,
          state: values.state,
          country: values.country,
          pincode: values.pincode,
          coordinates: {
            type: "Point",
            coordinates: [Number(values.longitude), Number(values.latitude)],
          },
        },
        socialLinks: {
          linkedin: values.linkedin?.trim() || "",
          instagram: values.instagram?.trim() || "",
          twitter: values.twitter?.trim() || "",
        },
        contactDetails: {
          contactName: values.contactName,
          phone: values.phone,
          email: values.email,
        },
      };

      await API.post("/businesses", {
        ...data,
        logo: uploadedLogo?.url,
        images: uploadedImages.map(img => img.url),
      });

      navigate("/success", {
        state: { type: "business" }
      });
      setLogoError(false);
      setImagesError(false);
    } catch (err) {
      message.error(
        err.response?.data?.message || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const clickedDropdown = e.target.closest(
        ".bbus-custom-select"
      );

      if (!clickedDropdown) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  return (
    <div className="bbus-page">
      <div className="bbus-container">
        <Card className="bbus-card">
          {/* Card Header - Back button on right side */}
          <div className="bbus-card-header">
            <div className="bbus-header-left" />
            <div className="bbus-header-center">
              <Title className="bbus-title">Add Business</Title>
            </div>
            <div className="bbus-header-right">
              <button className="bbus-back-btn" onClick={handleBack} type="button">
                <CloseOutlined />
              </button>
            </div>
          </div>

          {/* Stepper */}
          <div className="bbus-stepper">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`bbus-step ${current === i ? "active" : ""} ${current > i ? "done" : ""}`}
              >
                <div className="bbus-step-line-left" />
                <div className="bbus-step-inner">
                  <div className="bbus-circle">
                    {current > i ? (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  <span className="bbus-step-label">{step}</span>
                </div>
                <div className="bbus-step-line-right" />
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="bbus-progress-bar">
            <div
              className="bbus-progress-fill"
              style={{ width: `${(current / (steps.length - 1)) * 100}%` }}
            />
          </div>
          <div className="bbus-progress-label">
            Step {current + 1} of {steps.length} — {steps[current]}
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            scrollToFirstError
            validateTrigger="onSubmit"
            className="bbus-form"
          >
            {/* Step 0: Company */}
            <div style={getStepStyle(0)}>
              <Form.Item
                name="companyName"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Company Name
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter your company name" },
                  { min: 3, message: "Company name must be at least 3 characters" },
                  { max: 100, message: "Company name cannot exceed 100 characters" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9][A-Za-z0-9\s.&'-]*$/,
                    message: "Company name must start with a letter and can contain numbers, spaces, . & ' -",
                  },
                  { validator: checkCompanyName },
                ]}
              >
                <Input placeholder="e.g. Acme Corporation" />
              </Form.Item>

              <Form.Item
                name="tagline"
                label="Tagline"
                rules={[{ validator: validateTagline }]}
              >
                <Input placeholder="A short line that describes your business" />
              </Form.Item>

              <Form.Item
                name="description"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Description
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please provide a description" },
                  { min: 20, message: "Description should be at least 20 characters" },
                ]}
              >
                <TextArea rows={5} placeholder="Describe your business, services, products, and what makes you unique..." />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="companyUrl"
                    label="Company URL"
                    rules={[{ validator: validateUrlOptional }]}
                  >
                    <Input placeholder="https://company.com" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="website"
                    label="Website"
                    rules={[{ validator: validateUrlOptional }]}
                  >
                    <Input placeholder="https://website.com" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="foundedYear"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Founded Year
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the founded year" },
                  {
                    pattern: /^\d{4}$/,
                    message: "Enter a valid 4-digit year",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const year = Number(value);
                      const currentYear = new Date().getFullYear();
                      if (year < 1800 || year > currentYear) {
                        return Promise.reject(`Year must be between 1800 and ${currentYear}`);
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input maxLength={4} placeholder="e.g. 2015" />
              </Form.Item>
            </div>

            {/* Step 1: Business */}
            <div style={getStepStyle(1)}>
              <Form.Item
                name="industry"
                className={
                  openDropdown === "industry"
                    ? "dropdown-active"
                    : ""
                }
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Industry Type
                  </span>
                }
                required
                rules={[{ required: true, message: "Please select an industry" }]}
              >
                <div className="bbus-custom-select">
                  <div
                    className="bbus-custom-select-trigger"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "industry"
                          ? null
                          : "industry"
                      )
                    }
                  >
                    {form.getFieldValue("industry")
                      ? form
                        .getFieldValue("industry")
                        .split("_")
                        .map(
                          (word) =>
                            word.charAt(0).toUpperCase() +
                            word.slice(1)
                        )
                        .join(" ")
                      : "Select industry"}

                    <span className="bbus-custom-arrow">
                      ⌄
                    </span>
                  </div>

                  {openDropdown === "industry" && (
                    <div className="bbus-custom-dropdown">
                      {INDUSTRY_OPTIONS.map((item) => (
                        <div
                          key={item}
                          className="bbus-custom-dropdown-item"
                          onClick={() => {
                            form.setFieldValue(
                              "industry",
                              item
                            );

                            if (item === "other") {
                              setShowCustomIndustry(true);
                            } else {
                              setShowCustomIndustry(false);
                              setCustomIndustry("");
                            }

                            setOpenDropdown(null);
                          }}
                        >
                          {item
                            .split("_")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1)
                            )
                            .join(" ")}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
              {showCustomIndustry && (
                <Form.Item
                  name="customIndustry"
                  label="Enter Custom Industry"
                  rules={[
                    { required: true, message: "Please enter custom industry" },
                    { min: 2, message: "Industry must be at least 2 characters" },
                    { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" }
                  ]}
                  style={{ marginTop: -8 }}
                >
                  <Input
                    placeholder="e.g. Manufacturing, Hospitality, Banking"
                    value={customIndustry}
                    onChange={(e) => {
                      setCustomIndustry(e.target.value);
                      form.setFieldValue("industry", e.target.value);
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="businessType"
                className={
                  openDropdown === "businessType"
                    ? "dropdown-active"
                    : ""
                }
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Business Type
                  </span>
                }
                required
                rules={[{ required: true, message: "Please select a business type" }]}
              >
                <div className="bbus-custom-select">
                  <div
                    className="bbus-custom-select-trigger"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "businessType"
                          ? null
                          : "businessType"
                      )
                    }
                  >
                    {form.getFieldValue("businessType")
                      ? form
                        .getFieldValue("businessType")
                        .charAt(0)
                        .toUpperCase() +
                      form
                        .getFieldValue("businessType")
                        .slice(1)
                      : "Select business type"}

                    <span className="bbus-custom-arrow">
                      ⌄
                    </span>
                  </div>

                  {openDropdown === "businessType" && (
                    <div className="bbus-custom-dropdown">
                      {BUSINESS_TYPE_OPTIONS.map((item) => (
                        <div
                          key={item}
                          className="bbus-custom-dropdown-item"
                          onClick={() => {
                            form.setFieldValue(
                              "businessType",
                              item
                            );

                            if (item === "other") {
                              setShowCustomBusinessType(true);
                            } else {
                              setShowCustomBusinessType(false);
                              setCustomBusinessType("");
                            }

                            setOpenDropdown(null);
                          }}
                        >
                          {item.charAt(0).toUpperCase() +
                            item.slice(1)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
              {showCustomBusinessType && (
                <Form.Item
                  name="customBusinessType"
                  label="Enter Custom Business Type"
                  rules={[
                    { required: true, message: "Please enter custom business type" },
                    { min: 2, message: "Business type must be at least 2 characters" },
                    { pattern: /^[A-Za-z][A-Za-z\s&-]*$/, message: "Start with letter, can include letters, spaces, & and -" }
                  ]}
                  style={{ marginTop: -8 }}
                >
                  <Input
                    placeholder="e.g. LLP, Trust, Society"
                    value={customBusinessType}
                    onChange={(e) => {
                      setCustomBusinessType(e.target.value);
                      form.setFieldValue("businessType", e.target.value);
                    }}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="companySize"
                className={
                  openDropdown === "companySize"
                    ? "dropdown-active"
                    : ""
                }
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Company Size
                  </span>
                }
                required
                rules={[{ required: true, message: "Please select a company size" }]}
              >
                <div className="bbus-custom-select">
                  <div
                    className="bbus-custom-select-trigger"
                    onClick={() =>
                      setOpenDropdown(
                        openDropdown === "companySize"
                          ? null
                          : "companySize"
                      )
                    }
                  >
                    {form.getFieldValue("companySize")
                      ? `${form.getFieldValue(
                        "companySize"
                      )} employees`
                      : "Select company size"}

                    <span className="bbus-custom-arrow">
                      ⌄
                    </span>
                  </div>

                  {openDropdown === "companySize" && (
                    <div className="bbus-custom-dropdown">
                      {COMPANY_SIZE_OPTIONS.map((item) => (
                        <div
                          key={item}
                          className="bbus-custom-dropdown-item"
                          onClick={() => {
                            form.setFieldValue(
                              "companySize",
                              item
                            );

                            setOpenDropdown(null);
                          }}
                        >
                          {item} employees
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Form.Item>
            </div>

            {/* Step 2: Location */}
            <div style={getStepStyle(2)}>
              <Form.Item
                name="address"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Address
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the Address" },
                  {
                    pattern: /^[A-Za-z0-9\s,./#()&-]+$/,
                    message: "Please enter a valid address",
                  },
                ]}
              >
                <Input placeholder="e.g. #12, MG Road, Bengaluru" />
              </Form.Item>

              {/* LOCALITY FIELD — using LocationField for suggestions, same as CommercialSpace */}
              <Form.Item
                name="locality"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Locality
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter locality" },
                  { min: 2, message: "Locality must be at least 2 characters" },
                  { max: 100, message: "Locality cannot exceed 100 characters" },
                  {
                    pattern: /^(?=.*[A-Za-z])[A-Za-z0-9\s.,#&()-]+$/,
                    message: "Locality must contain at least one letter and can include numbers and symbols like . , # & ( ) -",
                  },
                ]}
              >
                <LocationField
                  form={form}
                  message={message}
                  setAutoDetecting={setAutoDetecting}
                />
              </Form.Item>

              <Form.Item
                name="city"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    City
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter the city" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "City name should contain only letters" },
                ]}
              >
                <Input placeholder="e.g. Bengaluru" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="state"
                    label={
                      <span className="bbus-label-with-badge">
                        <span className="bbus-required-star">*</span>
                        State
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the State" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "State name should contain only letters" },
                    ]}
                  >
                    <Input placeholder="e.g. Karnataka" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="country"
                    label={
                      <span className="bbus-label-with-badge">
                        <span className="bbus-required-star">*</span>
                        Country
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter the country" },
                      { pattern: /^[A-Za-z\s.-]+$/, message: "Country name should contain only letters" },
                    ]}
                  >
                    <Input placeholder="e.g. India" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="pincode"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Pincode
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Pincode is required" },
                  { pattern: /^[1-9][0-9]{5}$/, message: "Enter a valid 6-digit pincode" },
                ]}
              >
                <Input maxLength={6} placeholder="e.g. 560038" style={{ maxWidth: 200 }} />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="latitude"
                    label={
                      <span className="bbus-label-with-badge">
                        <span className="bbus-required-star">*</span>
                        Latitude
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Latitude is required" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "")
                            return Promise.reject("Latitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid latitude");
                          if (value < -90 || value > 90)
                            return Promise.reject("Latitude must be between -90 and 90");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-90}
                      max={90}
                      step={0.000001}
                      placeholder="e.g. 12.971891"
                      onBlur={() => {
                        const val = form.getFieldValue("latitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ latitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="longitude"
                    label={
                      <span className="bbus-label-with-badge">
                        <span className="bbus-required-star">*</span>
                        Longitude
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Longitude is required" },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null || value === "")
                            return Promise.reject("Longitude is required");
                          const str = value.toString();
                          if ((str.match(/\./g) || []).length > 1)
                            return Promise.reject("Only one decimal point allowed");
                          const parts = str.split(".");
                          if (parts[1] && parts[1].length > 6)
                            return Promise.reject("Max 6 digits allowed after decimal");
                          if (typeof value !== "number" || Number.isNaN(value))
                            return Promise.reject("Enter a valid longitude");
                          if (value < -180 || value > 180)
                            return Promise.reject("Longitude must be between -180 and 180");
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={-180}
                      max={180}
                      step={0.000001}
                      placeholder="e.g. 77.594563"
                      onBlur={() => {
                        const val = form.getFieldValue("longitude");
                        if (typeof val === "number") {
                          form.setFieldsValue({ longitude: Number(val.toFixed(6)) });
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 3: Contact */}
            <div style={getStepStyle(3)}>
              <Form.Item
                name="contactName"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Contact Person Name
                  </span>
                }
                required
                rules={[
                  { required: true, message: "Please enter contact name" },
                  { pattern: /^[A-Za-z\s.-]+$/, message: "Name can only contain letters and basic characters" },
                ]}
              >
                <Input placeholder="e.g. Rajesh Kumar" />
              </Form.Item>

              <Row gutter={[16, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="phone"
                    label={
                      <span className="bbus-label-with-badge">
                        <span className="bbus-required-star">*</span>
                        Phone Number
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter a phone number" },
                      { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit Indian phone number" },
                    ]}
                  >
                    <Input maxLength={10} placeholder="e.g. 9876543210" />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item
                    name="email"
                    label={
                      <span className="bbus-label-with-badge">
                        <span className="bbus-required-star">*</span>
                        Email Address
                      </span>
                    }
                    required
                    rules={[
                      { required: true, message: "Please enter your email address" },
                      { type: "email", message: "Enter a valid email address" },
                    ]}
                  >
                    <Input placeholder="e.g. contact@company.com" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* Step 4: Social */}
            <div style={getStepStyle(4)}>
              <Form.Item
                name="linkedin"
                label="LinkedIn"
                rules={[{ validator: validateSocialUrl("LinkedIn") }]}
              >
                <Input placeholder="https://linkedin.com/company/your-brand" />
              </Form.Item>

              <Form.Item
                name="instagram"
                label="Instagram"
                rules={[{ validator: validateSocialUrl("Instagram") }]}
              >
                <Input placeholder="https://instagram.com/yourbrand" />
              </Form.Item>

              <Form.Item
                name="twitter"
                label="Twitter / X"
                rules={[{ validator: validateSocialUrl("Twitter / X") }]}
              >
                <Input placeholder="https://twitter.com/yourbrand" />
              </Form.Item>
            </div>

            {/* Step 5: Images */}
            <div style={getStepStyle(5)}>
              <Form.Item
                required
                name="logo"
                valuePropName="fileList"
                getValueFromEvent={(e) => e?.fileList}
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Business Logo
                    <span className="bbus-badge-primary">Main Image</span>
                  </span>
                }
                rules={[
                  { required: true, message: "Please upload a business logo" },
                  {
                    validator: (_, value) => {
                      if (!value || value.length === 0) {
                        return Promise.reject("Please upload a business logo");
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateStatus={logoError ? "error" : ""}
                help={logoError ? "Please upload a business logo" : ""}
              >
                <Upload.Dragger
                  maxCount={1}
                  accept="image/png,image/jpeg"
                  className="bbus-upload"
                  beforeUpload={(file) => {
                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG, JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadLogo(file);
                    return false;
                  }}
                  onChange={() => {
                    setLogoError(false);
                  }}
                >
                  <div className="bbus-upload-inner">
                    <p className="bbus-upload-title">Upload Business Logo</p>
                    <p className="bbus-upload-hint">Square image recommended · PNG, JPEG</p>
                  </div>
                </Upload.Dragger>
              </Form.Item>

              <Form.Item
                required
                name="images"
                label={
                  <span className="bbus-label-with-badge">
                    <span className="bbus-required-star">*</span>
                    Business Images
                    <span className="bbus-badge-secondary">Up to 5</span>
                  </span>
                }
                validateStatus={imagesError ? "error" : ""}
                help={imagesError ? "Please upload at least one business image" : ""}
              >
                <Upload.Dragger
                  fileList={imageFileList}
                  multiple
                  accept="image/png,image/jpeg"
                  className="bbus-upload"

                  beforeUpload={(file) => {

                    const isValidType = ["image/png", "image/jpeg"].includes(file.type);

                    if (!isValidType) {
                      message.error("Only PNG and JPEG files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    uploadImages([file]);

                    setImagesError(false);

                    return false;
                  }}

                  onRemove={(file) => {
                    removeImage(file.public_id);
                  }}
                >
                  {imageFileList.length >= 5 ? null : (
                    <div className="bbus-upload-inner">

                      <p className="bbus-upload-title">
                        Upload Business Images
                      </p>

                      <p className="bbus-upload-hint">
                        Drag & drop images here or click to browse
                      </p>

                      <p className="bbus-upload-hint">
                        PNG, JPEG · Maximum 5 images
                      </p>

                    </div>
                  )}
                </Upload.Dragger>
              </Form.Item>
            </div>

            {/* Navigation Buttons */}
            <div className="bbus-btns">
              {current > 0 && (
                <Button
                  onClick={prev}
                  className="bbus-btn-prev"
                  disabled={autoDetecting}
                >
                  ← Previous
                </Button>
              )}
              <div className="bbus-btns-right">
                {current < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={next}
                    className="bbus-btn-next"
                    disabled={autoDetecting}
                  >
                    Next →
                  </Button>
                )}
                {current === steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={async () => {
                      const isValid = await validateCurrentStep();
                      if (!isValid) return;
                      form.submit();
                    }}
                    loading={loading}
                    disabled={loading}
                    className="bbus-btn-submit"
                  >
                    Submit Listing
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}

export default AddBusiness;